<script setup lang="ts">
import { ref, watch } from "vue";

import { useDebounce } from "@/composables/useDebounce";
import { concat } from "@/helpers/string";
import { addressDataGouvRequest } from "@/services/address";

import type { IAddress, IGeocodeJSONFeature } from '@/types/address';
import type { SelectOption } from "./types";

import Autocomplete from "./Autocomplete.vue";

interface AddressAutocompleteProps {
    label: string;
    modelValue: string;
    width?: string;
    error?: string;
    disabled?: boolean;
    enableLabel?: boolean;
    description?: string | boolean;
    required?: boolean;
    alt?: boolean;
    size?: "xs" | "sm" | "md" | "lg";
    variant?: "ghost" | "stroke" | "transparent" | "grey" | "grey2";
    placeholder?: string;
    focused?: boolean;
    iconLeft?: string;
    fitContent?: boolean;
    name?: string;
}
const props = withDefaults(defineProps<AddressAutocompleteProps>(), {
    focused: undefined,
});

const emit = defineEmits<{
    (event: "address", value: IAddress | null): void;
    (event: "update:modelValue", value: string): void;
}>();

const options = ref<SelectOption[]>([]);

const debounced = useDebounce(async () => {
    const features = await addressDataGouvRequest(props.modelValue);
    options.value = features?.map((feature: IGeocodeJSONFeature) => ({ label: feature.properties.label, value: feature }));
}, 500);

watch(() => props.modelValue, debounced);

const formatAddress = (address: IGeocodeJSONFeature): IAddress | null => {
    if (!address) return null;
    const { street, housenumber, postcode, name, city } = address.properties;
    const [longitude, latitude] = address.geometry.coordinates;

    return {
        name,
        city,
        zip: postcode,
        countryCode: "FR",
        location: [longitude, latitude],
        street: concat([housenumber, street]).trim(),
    };
};

const handleSelect = (data: { value: IGeocodeJSONFeature; label: string }) => {
    emit("update:modelValue", data.label);
    emit("address", formatAddress(data.value));
};

const handleChange = (value: string) => {
    emit("update:modelValue", value);
    if (!value) options.value = [];
};
</script>
<template>
    <Autocomplete
        :icon-left="iconLeft"
        :label="label"
        :modelValue="modelValue"
        :error="error"
        :width="width"
        :filter="false"
        :options="options"
        :disabled="disabled"
        :required="required"
        :description="description"
        :enableLabel="enableLabel"
        :alt="alt"
        :variant="variant"
        :placeholder="placeholder"
        :focused="focused"
        :fitContent="fitContent"
        :name="name"
        :size="size"
        @update:model-value="handleChange"
        @select="handleSelect"
    />
</template>
